// Settings › Data export (D115, APS-200; no Console D mock: Mosaic's direction, from the
// settings patterns). Owners and admins export a project's logs, traces and metrics over a range
// of UTC days as gzipped OTLP/JSON files; the job runs it (one at a time per organization, five
// a day) and emails who asked, with a link here. A done export's files download through links
// that work for 15 minutes, asked for each time the list opens; the files go 7 days after. Members
// see that only owners and admins do this (the list is theirs alone, row-level security).
import { useQuery } from '@tanstack/react-query'
import { Fragment, useEffect, useId, useRef, useState } from 'react'
import { isApiError, notConfigured } from '../api/client'
import {
  exportFilesQuery,
  exportsQuery,
  membersQuery,
  orgQuery,
  useCreateExport,
} from '../api/queries'
import type { ExportSignal, Member, OtlpExport } from '../api/types'
import { Btn, EmptyNote, PageHead, ScrollX, Section, Status, type StatusKey } from '../components/d'
import { Icon } from '../components/icons'
import { ErrorState, Loading } from '../components/states'
import { type MessageId, type Translator, useI18n } from '../i18n'
import { dayTime } from '../lib/format'

export const EXPORT_DOCS = 'https://docs.apsio.io/data-export'
const SIGNALS: readonly ExportSignal[] = ['logs', 'traces', 'metrics']
const SIGNAL_KEY: Record<ExportSignal, MessageId> = {
  logs: 'export.signal.logs',
  traces: 'export.signal.traces',
  metrics: 'export.signal.metrics',
}
/** The API's bound (MAX_RANGE_DAYS): From and To are both in, so 31 days at most. */
export const MAX_DAYS = 31
const DAY_MS = 86_400_000
const YMD = /^(\d{4})-(\d{2})-(\d{2})$/

/** A UTC day typed as YYYY-MM-DD, as its midnight in ms; NaN when it is not one. */
export function parseDay(text: string): number {
  const m = YMD.exec(text.trim())
  if (!m) return Number.NaN
  const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  // 2026-02-31 rolls over: not a day.
  return new Date(ms).toISOString().slice(0, 10) === text.trim() ? ms : Number.NaN
}

const ymd = (ms: number) => new Date(ms).toISOString().slice(0, 10)
const todayUtc = (now: number) => Math.floor(now / DAY_MS) * DAY_MS

export interface RangeCheck {
  from?: MessageId | undefined
  to?: MessageId | undefined
  /** The range starts before the detail retention: some signals are missing, not an error. */
  older?: boolean
  /** The API's [from, to): From's midnight, and the end of To (now, for today). */
  range?: { from: string; to: string }
}

/**
 * Checks a range of UTC days as the API will: To on or after From, at most 31 days, not after
 * today, and not wholly before the longest retention (nothing could be in it).
 */
export function checkRange(
  fromText: string,
  toText: string,
  retention: { detailDays: number; crashDays: number },
  now: number = Date.now(),
): RangeCheck {
  const from = parseDay(fromText)
  const to = parseDay(toText)
  const today = todayUtc(now)
  const out: RangeCheck = {}
  if (Number.isNaN(from)) out.from = 'export.err.day'
  else if (from > today) out.from = 'export.err.future'
  if (Number.isNaN(to)) out.to = 'export.err.day'
  else if (to > today) out.to = 'export.err.future'
  else if (!Number.isNaN(from) && to < from) out.to = 'export.err.order'
  else if (!Number.isNaN(from) && to - from >= MAX_DAYS * DAY_MS) out.to = 'export.err.long'
  else if (to < today - retention.crashDays * DAY_MS) out.to = 'export.err.retention'
  if (out.from || out.to) return out
  out.older = from < today - retention.detailDays * DAY_MS
  out.range = {
    from: new Date(from).toISOString(),
    to: new Date(Math.min(to + DAY_MS, now)).toISOString(),
  }
  return out
}

/** A size as the file list shows it: B, KiB or MiB. */
export function size(i: Translator, bytes: number): string {
  if (bytes < 1024) return `${i.number(bytes)} B`
  if (bytes < 1024 * 1024) return `${i.number(bytes / 1024, { maximumFractionDigits: 1 })} KiB`
  return `${i.number(bytes / 1024 / 1024, { maximumFractionDigits: 1 })} MiB`
}

/** A UTC day as a table shows it: "Oct 1" this year, with the year otherwise. */
function utcDay(i: Translator, ms: number, now: number): string {
  const sameYear = new Date(ms).getUTCFullYear() === new Date(now).getUTCFullYear()
  return i.date(
    new Date(ms).toISOString(),
    sameYear
      ? { month: 'short', day: 'numeric', timeZone: 'UTC' }
      : { dateStyle: 'medium', timeZone: 'UTC' },
  )
}

/** The days an export covers, From to To: its end is exclusive. */
export function rangeLabel(i: Translator, e: Pick<OtlpExport, 'from' | 'to'>, now: number): string {
  return `${utcDay(i, Date.parse(e.from), now)} → ${utcDay(i, Date.parse(e.to) - 1, now)}`
}

export type ExportState =
  | 'queued'
  | 'running'
  | 'done'
  | 'expired'
  | 'read_failed'
  | 'store_failed'
  | 'failed'
  | 'project_gone'
  | 'erased'

/** What the table says of an export: a done one past its 7 days is expired, a failure in words. */
export function exportState(e: OtlpExport, now: number): ExportState {
  if (e.status !== 'done' && e.status !== 'failed') return e.status
  if (e.status === 'done')
    return e.expires_at && Date.parse(e.expires_at) > now ? 'done' : 'expired'
  if (
    e.error === 'read_failed' ||
    e.error === 'store_failed' ||
    e.error === 'project_gone' ||
    e.error === 'erased'
  )
    return e.error
  return 'failed'
}

const STATE: Record<ExportState, { s: StatusKey; glyph: string; label: MessageId }> = {
  queued: { s: 'mute', glyph: '○', label: 'export.status.queued' },
  running: { s: 'reg', glyph: '◐', label: 'export.status.running' },
  done: { s: 'res', glyph: '✓', label: 'export.status.done' },
  expired: { s: 'mute', glyph: '–', label: 'export.status.expired' },
  read_failed: { s: 'fire', glyph: '×', label: 'export.status.readFailed' },
  store_failed: { s: 'fire', glyph: '×', label: 'export.status.storeFailed' },
  failed: { s: 'fire', glyph: '×', label: 'export.status.failed' },
  project_gone: { s: 'mute', glyph: '–', label: 'export.status.projectGone' },
  erased: { s: 'mute', glyph: '–', label: 'export.status.erased' },
}

const total = (e: OtlpExport) => Object.values(e.counts).reduce((a, n) => a + (n ?? 0), 0)

function NewExportForm({
  orgId,
  projectId,
  running,
  retention,
  onClose,
}: {
  orgId: string
  projectId: string
  /** The export under way in this project, if any, and who started it. */
  running: { at: string; by: string | null } | null
  retention: { detailDays: number; crashDays: number }
  onClose: () => void
}) {
  const i = useI18n()
  const { t } = i
  const create = useCreateExport(orgId, projectId)
  const now = Date.now()
  const [signals, setSignals] = useState<ExportSignal[]>([...SIGNALS])
  const [fromText, setFrom] = useState(ymd(todayUtc(now) - 6 * DAY_MS))
  const [toText, setTo] = useState(ymd(todayUtc(now)))
  // Errors show once a field was left (validated on blur), and all of them on Export.
  const [touched, setTouched] = useState({ from: false, to: false })
  const ids = { title: useId(), from: useId(), to: useId(), fromErr: useId(), toErr: useId() }
  // The window opens with the focus in it, on its first field.
  const first = useRef<HTMLInputElement>(null)
  useEffect(() => first.current?.focus(), [])
  const check = checkRange(fromText, toText, retention)
  const fromErr = touched.from ? check.from : undefined
  const toErr = touched.to ? check.to : undefined
  const blocked = running !== null
  const submit = () => {
    setTouched({ from: true, to: true })
    if (!check.range || signals.length === 0 || blocked) return
    create.mutate({ signals, ...check.range }, { onSuccess: onClose })
  }
  return (
    <form
      className="win dx-form"
      aria-labelledby={ids.title}
      noValidate
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
    >
      <div className="win-bar">
        <b id={ids.title}>{t('export.new')}</b>
        <span className="r">
          <Btn size="sm" variant="ghost" aria-label={t('export.close')} onClick={onClose}>
            <Icon name="x" small />
          </Btn>
        </span>
      </div>
      <div className="dx-fields">
        <fieldset className="dx-signals">
          <legend className="lbl">{t('export.signals')}</legend>
          {SIGNALS.map((s) => (
            <label key={s} className="dx-check">
              <input
                ref={s === SIGNALS[0] ? first : undefined}
                type="checkbox"
                checked={signals.includes(s)}
                onChange={(e) =>
                  setSignals(
                    e.target.checked
                      ? SIGNALS.filter((x) => x === s || signals.includes(x))
                      : signals.filter((x) => x !== s),
                  )
                }
              />
              {t(SIGNAL_KEY[s])}
            </label>
          ))}
          {signals.length === 0 ? (
            <span className="dx-err" role="alert">
              {t('export.err.signals')}
            </span>
          ) : null}
        </fieldset>
        <div className="lbl">
          <label htmlFor={ids.from}>{t('export.from')}</label>
          <input
            id={ids.from}
            // A date picker (D121), in UTC days; the check below still says what is wrong.
            type="date"
            className="input mono"
            max={ymd(todayUtc(now))}
            autoComplete="off"
            value={fromText}
            aria-invalid={fromErr ? true : undefined}
            aria-describedby={fromErr ? ids.fromErr : undefined}
            onChange={(e) => setFrom(e.target.value)}
            onBlur={() => setTouched((x) => ({ ...x, from: true }))}
          />
          {fromErr ? (
            <span id={ids.fromErr} className="dx-err">
              {t(fromErr)}
            </span>
          ) : null}
        </div>
        <div className="lbl">
          <label htmlFor={ids.to}>{t('export.to')}</label>
          <input
            id={ids.to}
            // A date picker (D121), in UTC days; the check below still says what is wrong.
            type="date"
            className="input mono"
            max={ymd(todayUtc(now))}
            autoComplete="off"
            value={toText}
            aria-invalid={toErr ? true : undefined}
            aria-describedby={toErr ? ids.toErr : undefined}
            onChange={(e) => setTo(e.target.value)}
            onBlur={() => setTouched((x) => ({ ...x, to: true }))}
          />
          {toErr ? (
            <span id={ids.toErr} className="dx-err">
              {t(toErr, {
                max: MAX_DAYS,
                date: utcDay(i, todayUtc(now) - retention.crashDays * DAY_MS, now),
              })}
            </span>
          ) : null}
        </div>
      </div>
      {check.older ? (
        <p className="se-hint dx-line">{t('export.older', { days: retention.detailDays })}</p>
      ) : null}
      <div className="dx-foot">
        {running ? (
          <p className="se-hint dx-line">
            {running.by
              ? t('export.runningBy', { time: dayTime(i, running.at), name: running.by })
              : t('export.running', { time: dayTime(i, running.at) })}
          </p>
        ) : null}
        {create.error ? (
          <p role="alert" className="se-hint dx-line">
            <Status
              s="miss"
              label={t(
                notConfigured(create.error)
                  ? 'export.err.notConfigured'
                  : isApiError(create.error, 409)
                    ? 'export.err.busy'
                    : isApiError(create.error, 429)
                      ? 'export.err.daily'
                      : isApiError(create.error, 400)
                        ? 'export.err.invalid'
                        : isApiError(create.error, 403)
                          ? 'export.managersOnly'
                          : 'state.failed',
              )}
            />
          </p>
        ) : null}
        <div className="dx-btns">
          <Btn variant="ghost" onClick={onClose}>
            {t('export.cancel')}
          </Btn>
          <button type="submit" className="btn solid" disabled={blocked || create.isPending}>
            {t('export.submit')}
          </button>
        </div>
      </div>
    </form>
  )
}

/** A done export's files, under its row: fresh links each time it opens. */
function Files({ orgId, projectId, e }: { orgId: string; projectId: string; e: OtlpExport }) {
  const i = useI18n()
  const { t } = i
  const files = useQuery(exportFilesQuery(orgId, projectId, e.id))
  if (files.isPending) return <Loading />
  if (files.isError)
    return isApiError(files.error, 410) ? (
      <p className="se-hint dx-line">{t('export.files.gone')}</p>
    ) : (
      <ErrorState error={files.error} retry={() => files.refetch()} />
    )
  const d = files.data
  const time = i.date(d.links_expire_at, { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' })
  return (
    <>
      <table className="t dx-ft">
        <caption className="visually-hidden">{t('export.files.list')}</caption>
        <thead>
          <tr>
            <th className="f">{t('export.files.name')}</th>
            <th>{t('export.files.signal')}</th>
            <th className="num dx-lg">{t('export.files.size')}</th>
            <th className="num last dx-lg">{t('export.files.records')}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="f">
              <a href={d.manifest_url} className="mono" translate="no" download>
                manifest.json
              </a>
              {/* A phone: size and records on a second line (the columns step out). */}
              <span className="t2 dx-sm">{size(i, d.manifest_bytes)}</span>
            </td>
            <td className="faint">–</td>
            <td className="num dx-lg">{size(i, d.manifest_bytes)}</td>
            <td className="num last faint dx-lg">–</td>
          </tr>
          {d.files.map((f) => (
            <tr key={f.name}>
              <td className="f">
                <a href={f.url} className="mono" translate="no" download>
                  {f.name}
                </a>
                <span className="t2 dx-sm">
                  {t('export.files.sizeRecords', { size: size(i, f.bytes), records: f.records })}
                </span>
              </td>
              <td>{t(SIGNAL_KEY[f.signal])}</td>
              <td className="num dx-lg">{size(i, f.bytes)}</td>
              <td className="num last dx-lg">{i.number(f.records)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="se-hint dx-line">{t('export.files.links', { time })}</p>
    </>
  )
}

function Exports({
  orgId,
  projectId,
  rows,
  names,
}: {
  orgId: string
  projectId: string
  rows: OtlpExport[]
  names: Map<string, string>
}) {
  const i = useI18n()
  const { t } = i
  const now = Date.now()
  const [open, setOpen] = useState<string | null>(null)
  const cols = 7
  return (
    <ScrollX label={t('table.of', { name: t('export.list') })}>
      <table className="t dx-t" aria-label={t('export.list')}>
        <thead>
          <tr>
            <th className="f">{t('export.col.started')}</th>
            <th className="dx-lg">{t('export.col.signals')}</th>
            <th className="dx-lg">{t('export.col.range')}</th>
            <th>{t('export.col.status')}</th>
            <th className="num dx-lg">{t('export.col.records')}</th>
            <th className="nowrap dx-lg">{t('export.col.until')}</th>
            <th className="last">
              <span className="visually-hidden">{t('settings.keys.actions')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((e) => {
            const state = exportState(e, now)
            const st = STATE[state]
            const by = e.requested_by ? names.get(e.requested_by) : undefined
            const shown = open === e.id && state === 'done'
            return (
              <Fragment key={e.id}>
                <tr>
                  <td className="f">
                    {dayTime(i, e.created_at)}
                    {/* A phone: the days and signals under the time (their columns step out). */}
                    <span className="t2 dx-sm">
                      {/* Two chunks, so a narrow line breaks between them (Mosaic). */}
                      <span className="nowrap">{rangeLabel(i, e, now)}</span>
                      {' · '}
                      <span className="nowrap">
                        {e.signals.map((s) => t(SIGNAL_KEY[s])).join(' · ')}
                      </span>
                    </span>
                    {by ? <span className="t2">{t('export.by', { name: by })}</span> : null}
                  </td>
                  <td className="dx-lg">{e.signals.map((s) => t(SIGNAL_KEY[s])).join(' · ')}</td>
                  <td className="nowrap dx-lg">{rangeLabel(i, e, now)}</td>
                  <td>
                    <Status s={st.s} glyph={st.glyph} label={t(st.label)} />
                  </td>
                  <td className="num dx-lg">
                    {e.status === 'done' ? (
                      <>
                        {i.number(total(e))}
                        <span className="t2">
                          {e.signals.map((s, k) => (
                            <Fragment key={s}>
                              {k > 0 ? ' · ' : null}
                              <span className="nowrap">
                                {t(SIGNAL_KEY[s])} {i.number(e.counts[s] ?? 0)}
                              </span>
                            </Fragment>
                          ))}
                        </span>
                      </>
                    ) : (
                      <span className="faint">–</span>
                    )}
                  </td>
                  <td className="nowrap dx-lg">
                    {state === 'done' && e.expires_at ? (
                      dayTime(i, e.expires_at)
                    ) : (
                      <span className="faint">–</span>
                    )}
                  </td>
                  <td className="last">
                    {state === 'done' ? (
                      <Btn
                        size="sm"
                        aria-expanded={shown}
                        onClick={() => setOpen(shown ? null : e.id)}
                      >
                        {t('export.download')}
                      </Btn>
                    ) : null}
                  </td>
                </tr>
                {shown ? (
                  <tr className="dx-open">
                    <td className="f" colSpan={cols}>
                      <Files orgId={orgId} projectId={projectId} e={e} />
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </ScrollX>
  )
}

export function DataExportSettings({
  orgId,
  projectId,
  crumb,
  title,
}: {
  orgId: string
  projectId: string
  crumb: string
  title: string
}) {
  const { t, rich } = useI18n()
  const org = useQuery(orgQuery(orgId))
  const role = org.data?.role
  const manager = role === 'owner' || role === 'admin'
  const list = useQuery({ ...exportsQuery(orgId, projectId), enabled: manager })
  const members = useQuery({ ...membersQuery(orgId), enabled: manager })
  const [formOpen, setFormOpen] = useState(false)
  const member = role === 'member' || isApiError(list.error, 403)
  const rows = list.data ?? []
  const names = new Map((members.data ?? []).map((m: Member) => [m.user_id, m.name || m.email]))
  const under = rows.find((e) => e.status === 'queued' || e.status === 'running')
  const running = under
    ? {
        at: under.started_at ?? under.created_at,
        by: under.requested_by ? (names.get(under.requested_by) ?? null) : null,
      }
    : null
  const retention = {
    detailDays: org.data?.retention_days ?? 30,
    crashDays: org.data?.crash_retention_days ?? org.data?.retention_days ?? 30,
  }
  return (
    <main id="main" className="pg se-pg data-export">
      <PageHead
        crumb={crumb}
        title={title}
        meta={t('export.meta')}
        actions={
          manager && !member ? (
            <button
              type="button"
              className="btn solid"
              aria-expanded={formOpen}
              onClick={() => setFormOpen(true)}
            >
              {t('export.new')}
            </button>
          ) : undefined
        }
      />
      {formOpen && manager && !member ? (
        <NewExportForm
          orgId={orgId}
          projectId={projectId}
          running={running}
          retention={retention}
          onClose={() => setFormOpen(false)}
        />
      ) : null}
      <div className="se-main">
        {org.isPending ? (
          <Loading />
        ) : org.isError ? (
          <ErrorState error={org.error} retry={() => org.refetch()} />
        ) : member ? (
          <p className="se-hint dx-line">{t('export.managersOnly')}</p>
        ) : (
          <Section
            title={t('export.list')}
            meta={rows.length > 0 ? t('export.count', { count: rows.length }) : undefined}
          >
            {list.isPending ? (
              <Loading />
            ) : list.isError ? (
              <ErrorState error={list.error} retry={() => list.refetch()} />
            ) : rows.length === 0 ? (
              <EmptyNote title={t('export.none')}>{t('export.noneBody')}</EmptyNote>
            ) : (
              <Exports orgId={orgId} projectId={projectId} rows={rows} names={names} />
            )}
          </Section>
        )}
        <p className="se-hint dx-line dx-format">
          <a href={EXPORT_DOCS} target="_blank" rel="noreferrer">
            {t('export.formatLink')}
          </a>
          {rich('export.formatRest')}
        </p>
      </div>
    </main>
  )
}
